export const createUISlice = (set, get) => ({
  notifications: [],
  achievements: {},
  pendingMonthlyReport: null,
  pendingAnnualReport: null,
  monthlyHistory: [],
  yearlyReports: [],
  activeResolution: null,
  tutorialStep: 0,
  // SCD Lot 0 — tutoriel SCD désactivé (référence flotte/contrats supprimés).
  tutorialComplete: true,
  /** Contextual tutorial state — see action `triggerContextualTutorial`. */
  contextualTutorialsSeen: {},
  activeContextualTuto: null,
  pendingPhaseUp: null,
  // 30/05/2026 — celebratory modal triggered when Midal / Réserve
  // fiscale produces a year-end refund. Consumed by <MarchettiBonusModal />.
  pendingMarchettiBonus: null,
  // 31/05/2026 — annual board meeting modal. Aggregates the support digest
  // messages into a single ceremonial moment at year-end. Consumed by
  // <BoardMeetingModal />. Shape: { year, messages: [{...digestPayload}] }.
  pendingBoardMeeting: null,
  // 31/05/2026 — mid-year strategic review (Jun 30). Purely informational
  // snapshot of H1 KPIs. Consumed by <MidYearBoardMeeting />.
  pendingMidYearReview: null,
  treasuryHistory: [],
  repHistory: [],
  alerts: [],
  activeChallenge: null,       // { id, startDay, completed, failed }
  completedChallenges: {},     // { [challengeId]: { completedAt, ... } }
  selectedAdvisor: null,       // 'maurice' | 'camille' | null
  advisorEnabled: true,        // toggle on/off
  competitorEvents: [],        // history of competition events
  pendingCompetitionEvent: null, // active event awaiting player choice

  startChallenge: (challengeId) => {
    const state = get();
    set({
      activeChallenge: { id: challengeId, startDay: state.totalDays, completed: false, failed: false },
    });
  },

  completeChallenge: () => {
    const state = get();
    if (!state.activeChallenge) return;
    set({
      completedChallenges: { ...state.completedChallenges, [state.activeChallenge.id]: { completedAt: state.totalDays } },
      activeChallenge: null,
    });
  },

  failChallenge: () => {
    set({ activeChallenge: null });
  },

  abandonChallenge: () => {
    set({ activeChallenge: null });
  },

  markNotificationRead: (notifId) => {
    const state = get();
    set({
      notifications: (state.notifications || []).map(n =>
        n.id === notifId ? { ...n, read: true } : n
      ),
    });
  },

  clearNotifications: () => {
    set({ notifications: [] });
  },

  dismissMonthlyReport: () => {
    set({ pendingMonthlyReport: null });
  },

  dismissAnnualReport: () => {
    set({ pendingAnnualReport: null });
  },

  /**
   * Apply a board-meeting resolution chosen by the player. The resolution
   * runs for the next 360 days (one fiscal year). Possible types:
   *   - 'dividend'      : distribute a FREE amount (22/07/2026 — retour user)
   *                       split between `opts.bonusAmount` (prime collabo-
   *                       rateurs → +satisfaction) and `opts.personalAmount`
   *                       (dividende personnel → patrimoine dirigeant,
   *                       rembourse d'abord l'apport). Sans opts (legacy),
   *                       30 % du résultat net en dividende personnel.
   *   - 'investment'    : modernize fleet → -8 % of net result (capped 250 k€);
   *                       active flag for 360 d → -10 % maintenance, -5 % fuel.
   *   - 'restructuring' : aggressive cost-cutting → no immediate cash cost,
   *                       but all drivers lose 5 satisfaction immediately;
   *                       active flag for 360 d → -8 % driver employer cost.
   *   - 'statu_quo'     : no-op (close the meeting without a vote).
   */
  applyBoardResolution: (type, opts) => {
    const state = get();
    const report = state.pendingAnnualReport;
    const net = Math.max(0, report?.netResult || 0);

    const patch = { pendingAnnualReport: null };

    if (type === 'dividend') {
      const hasOpts = !!opts && (Number.isFinite(opts.bonusAmount) || Number.isFinite(opts.personalAmount));
      const personalAmount = hasOpts ? Math.max(0, Math.round(opts.personalAmount || 0)) : Math.round(net * 0.30);
      const bonusAmount = hasOpts ? Math.max(0, Math.round(opts.bonusAmount || 0)) : 0;
      const payout = personalAmount + bonusAmount;
      patch.treasury = (state.treasury || 0) - payout;
      // +5 rép/noto si le total distribué atteint 10 % du résultat, +2 sinon.
      const imageBoost = payout > 0 ? (payout >= net * 0.10 ? 5 : 2) : 0;
      patch.reputation = Math.min(100, (state.reputation || 0) + imageBoost);
      patch.notoriety = Math.min(100, (state.notoriety || 0) + imageBoost);
      // 06/02/2026 — P1 retour user : la part personnelle rembourse en
      // priorité l'apport injecté lors d'une recapitalisation.
      const injected = state.personalCapitalInjected || 0;
      patch.personalCapitalInjected = Math.max(0, injected - personalAmount);
      const reimbursed = Math.min(personalAmount, injected);
      // 22/07/2026 — patrimoine dirigeant : cumul des dividendes personnels.
      patch.personalWealth = (state.personalWealth || 0) + personalAmount;
      // Prime collaborateurs → boost satisfaction proportionnel.
      let satBoost = 0;
      const driversArr = state.drivers || [];
      if (bonusAmount > 0 && driversArr.length > 0) {
        satBoost = Math.min(15, Math.max(1, Math.round((bonusAmount / driversArr.length) / 400)));
        patch.drivers = driversArr.map(d => ({
          ...d,
          satisfaction: Math.min(100, (d.satisfaction || 0) + satBoost),
        }));
      }
      patch.activeResolution = {
        type, year: report?.year, appliedAt: state.totalDays || 0,
        payout, bonusAmount, personalAmount, expiresAt: (state.totalDays || 0) + 360,
      };
      const reimburseSuffix = reimbursed > 0
        ? ` — dont ${reimbursed.toLocaleString('fr-FR')} € remboursés sur votre apport personnel`
        : '';
      const splitParts = [];
      if (bonusAmount > 0) splitParts.push(`${bonusAmount.toLocaleString('fr-FR')} € de primes collaborateurs (+${satBoost} satisfaction)`);
      if (personalAmount > 0) splitParts.push(`${personalAmount.toLocaleString('fr-FR')} € de dividende personnel`);
      const splitDetail = splitParts.length > 0 ? ` (${splitParts.join(', ')})` : '';
      patch.notifications = [...(state.notifications || []).slice(-30), {
        id: `res-div-${Date.now()}`,
        type: 'board_resolution',
        title: 'Dividende voté',
        message: `Conseil ${report?.year} : ${payout.toLocaleString('fr-FR')} € distribués${splitDetail}${reimburseSuffix}. +${imageBoost} réputation, +${imageBoost} notoriété.`,
        day: state.totalDays || 0,
        read: false,
      }];
    } else if (type === 'investment') {
      // 22/07/2026 — 20 % → 8 % du résultat, plafonné 250 k€ (retour user :
      // « 400 k€ pour −10 % entretien, c'est 40 % de mes charges année 1 »).
      const spend = Math.min(250000, Math.round(net * 0.08));
      patch.treasury = (state.treasury || 0) - spend;
      patch.activeResolution = {
        type, year: report?.year, appliedAt: state.totalDays || 0,
        spend, expiresAt: (state.totalDays || 0) + 360,
        // active effects consumed by coreSlice.nextDay
        maintenanceDiscount: 0.10,
        fuelDiscount: 0.05,
      };
      patch.notifications = [...(state.notifications || []).slice(-30), {
        id: `res-inv-${Date.now()}`,
        type: 'board_resolution',
        title: "Plan d'investissement validé",
        message: `Conseil ${report?.year} : ${spend.toLocaleString('fr-FR')} € investis dans la modernisation. -10 % entretien & -5 % carburant pendant 12 mois.`,
        day: state.totalDays || 0,
        read: false,
      }];
    } else if (type === 'restructuring') {
      patch.activeResolution = {
        type, year: report?.year, appliedAt: state.totalDays || 0,
        expiresAt: (state.totalDays || 0) + 360,
        salaryDiscount: 0.08,
      };
      patch.drivers = (state.drivers || []).map(d => ({
        ...d,
        satisfaction: Math.max(0, (d.satisfaction || 0) - 5),
      }));
      patch.notifications = [...(state.notifications || []).slice(-30), {
        id: `res-res-${Date.now()}`,
        type: 'board_resolution',
        title: 'Plan de restructuration lancé',
        message: `Conseil ${report?.year} : -8 % sur les salaires chauffeurs pendant 12 mois. Satisfaction de l'équipe -5 (immédiat).`,
        day: state.totalDays || 0,
        read: false,
      }];
    } else {
      // statu_quo — just record the absence of decision.
      patch.activeResolution = {
        type: 'statu_quo', year: report?.year, appliedAt: state.totalDays || 0,
        expiresAt: (state.totalDays || 0) + 360,
      };
    }

    // Stamp the resolution onto the matching yearlyReports entry so the
    // history shows what was decided each year.
    if (report?.year) {
      patch.yearlyReports = (state.yearlyReports || []).map(r =>
        r.year === report.year ? { ...r, resolution: patch.activeResolution } : r
      );
    }

    set(patch);
    return { ok: true };
  },

  advanceTutorial: () => {
    const state = get();
    set({ tutorialStep: (state.tutorialStep || 0) + 1 });
  },

  /**
   * Step the onboarding tutorial backwards by one. Bounded at 0 (no
   * negative steps). Used by the "Précédent" button added 28/05/2026
   * after user feedback that the action-gated only flow felt rigid.
   */
  goBackTutorial: () => {
    const state = get();
    const next = Math.max(0, (state.tutorialStep || 0) - 1);
    set({ tutorialStep: next });
  },

  skipTutorial: () => {
    set({ tutorialComplete: true, tutorialStep: null });
  },

  /**
   * Contextual tutorials — one-shot mini cards shown the FIRST time the
   * player encounters a mechanic (event, tax, credit, phase up, unhappy
   * driver, M&A, late payment). Each id is fired at most once per game.
   *
   * State : `contextualTutorialsSeen` = { firstEvent: true, ... }
   *         `activeContextualTuto`    = { id } | null
   *
   * The slices that own the mechanic call `triggerContextualTutorial(id)`
   * when the condition is met. The card itself is rendered globally by
   * `ContextualTutoCard.jsx` in the Layout.
   */
  triggerContextualTutorial: (id) => {
    const state = get();
    const seen = state.contextualTutorialsSeen || {};
    // Already seen this game → no-op. Already active → no-op too (prevents
    // a flood when several triggers fire on the same tick).
    if (seen[id] || state.activeContextualTuto) return;
    // Defense in depth (testing agent feedback 28/05/2026) : also block when
    // the linear onboarding tour is still running. ContextualTutoTriggers.jsx
    // applies the same gating at the React layer, but `crmSlice.applyClientEvent`
    // and any future direct callers bypass that React-side gate. Enforcing
    // here guarantees the rule no matter the call site.
    if (state.tutorialComplete === false && (state.tutorialStep !== null && state.tutorialStep !== undefined)) {
      return;
    }
    set({ activeContextualTuto: { id, openedAt: state.totalDays || 0 } });
  },

  dismissContextualTutorial: () => {
    const state = get();
    const active = state.activeContextualTuto;
    if (!active) return;
    set({
      contextualTutorialsSeen: { ...(state.contextualTutorialsSeen || {}), [active.id]: true },
      activeContextualTuto: null,
    });
  },

  dismissPhaseUp: () => {
    set({ pendingPhaseUp: null });
  },

  dismissMarchettiBonus: () => {
    set({ pendingMarchettiBonus: null });
  },

  dismissBoardMeeting: () => {
    set({ pendingBoardMeeting: null });
  },

  dismissMidYearReview: () => {
    set({ pendingMidYearReview: null });
  },

  setAdvisor: (advisorId) => {
    set({ selectedAdvisor: advisorId });
  },

  toggleAdvisor: () => {
    const state = get();
    set({ advisorEnabled: !state.advisorEnabled });
  },

  setAdvisorEnabled: (enabled) => {
    set({ advisorEnabled: enabled });
  },

  // Competition events
  triggerCompetitionEvent: (event) => {
    set({ pendingCompetitionEvent: event });
  },

  resolveCompetitionEvent: (choiceIndex) => {
    const state = get();
    const event = state.pendingCompetitionEvent;
    if (!event || !event.choices[choiceIndex]) return;

    const choice = event.choices[choiceIndex];
    const logEntry = {
      day: state.totalDays,
      title: event.title,
      outcome: choice.label,
      competitor: event.competitorName,
    };

    set({
      pendingCompetitionEvent: null,
      competitorEvents: [...state.competitorEvents, logEntry].slice(-30),
      treasury: state.treasury - (choice.cost || 0),
      reputation: Math.max(0, Math.min(100, (state.reputation || 50) + (choice.reputationChange || 0))),
    });
  },
});
